import { StackIcon } from "@xola/icons";
import { Spinner } from "@xola/ui-kit";
import { isNil } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Experience, Package, Seller } from "types";
import { makePackagePurchaseItem } from "../../models/CreatePurchaseState";
import {
    getExpandedExperiencesInPackage,
    getLowestDemographicPrice,
    hasPriceRange,
    hasSelectedProductsExpanded,
} from "../../models/Package";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Currency } from "../Common/Currency";
import { ProductImage } from "../Common/ProductImage";
import { SellerLogo } from "../Common/SellerLogo";
import { eraseArrivalCache } from "../DatePicker/useArrivalTimeslots";
import { ProductBadge } from "./ExperienceList";
import { useInvalidate } from "@/hooks/useInvalidation";
import { usePackageTimeslots } from "@/hooks/usePackageTimeslots";
import { useResource } from "@/hooks/useResource";

export const PackageTile = ({
    pkg,
    experiences,
    isPartner,
}: {
    pkg: Package;
    experiences: Experience[];
    isPartner?: boolean;
}) => {
    const [isSelected, setIsSelected] = useState(false);
    const price = getLowestDemographicPrice(pkg, experiences);

    const partnerExperience = experiences.find((experience) => experience.seller.id !== pkg.seller.id);
    const partnerSellerId = partnerExperience?.seller.id;

    const { data: partnerSeller } = useResource<Seller>(partnerSellerId ? ["users", partnerSellerId] : []);
    const handlePackageSelect = () => {
        setIsSelected(true);
    };

    return (
        <div
            data-testid="experience-list-item"
            className="flex cursor-pointer flex-row rounded p-2 hover:bg-secondary-hover"
            onClick={handlePackageSelect}
        >
            <ProductImage product={pkg} />
            <div className="flex flex-col space-y-1">
                <span className="flex items-center space-x-2 text-md font-bold">
                    <span>{pkg.name}</span>
                    {isSelected && <Spinner size="tiny" />}
                </span>

                {partnerSeller && isPartner && (
                    <span>
                        <ProductBadge
                            text={partnerSeller.name}
                            icon={<SellerLogo seller={partnerSeller} size="small" />}
                            color="primary"
                        />
                    </span>
                )}

                <div className="space-x-2">
                    {!isNil(price) && (
                        <ProductBadge icon={<StackIcon />} text={<PackageBadge pkg={pkg} price={price} />} />
                    )}
                </div>
            </div>

            {isSelected && <PackageSelectionHandler pkg={pkg} experiences={experiences} isPartner={isPartner} />}
        </div>
    );
};

const PackageSelectionHandler = ({
    pkg,
    experiences,
    isPartner,
}: {
    pkg: Package;
    experiences: Experience[];
    isPartner?: boolean;
}) => {
    const history = useHistory<{ isAddingItem?: boolean } | null>();

    const selectedOrganizer = useCreatePurchaseStore((state) => state.organizer);
    const isResumeBookingShown = useCreatePurchaseStore((state) => state.purchase.items.length > 0);
    const { resetState, addItem, setSeller, setActiveItemIndex } = useCreatePurchaseStore.getState().actions;

    const { data: timeslots, isLoading, isFetching } = usePackageTimeslots(pkg);
    const invalidate = useInvalidate();

    const createPackage = () => {
        if (!selectedOrganizer || isResumeBookingShown) {
            eraseArrivalCache(pkg.id);
            invalidate("timeslots");
            resetState();
            useCreatePurchaseStore.setState({ errors: undefined });
            if (isPartner) {
                useCreatePurchaseStore.setState({ isPartnerPackage: true });
            }
        }

        const packageExperiences = hasSelectedProductsExpanded(pkg)
            ? getExpandedExperiencesInPackage(pkg)
            : experiences;

        setSeller(pkg.seller.id);
        const packageData = makePackagePurchaseItem(pkg, packageExperiences, null, timeslots);
        for (const itemData of packageData) {
            addItem(itemData);
        }

        setActiveItemIndex(0);
        useCreatePurchaseStore.setState({ step: CreatePurchaseStep.SELECT_DATE });
        history.push("/cart");
    };

    useEffect(() => {
        if (!isLoading && !isFetching) {
            createPackage();
        }
        // Can't add createPackage to the dependency array because it will always change and can't put it in useCallback because it has a lot of depenencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isFetching]);

    return null;
};

const PackageBadge = ({ pkg, price }: { pkg: Package; price: number }) => {
    return (
        <span>
            {hasPriceRange(pkg) ? "From " : null}
            <Currency>{price}</Currency>
        </span>
    );
};
